import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import DriverForm from '../components/DriverForm';
import { confirmDialog } from '../utils/confirm';

const Drivers = () => {
  const navigate = useNavigate();
  const { searchQuery, isAdmin } = useAppContext();
  const [drivers, setDrivers] = useState([]);
  const [vehicleMap, setVehicleMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);

  // Credentials form state (admin only)
  const [credUsername, setCredUsername] = useState('');
  const [credPassword, setCredPassword] = useState('');
  const [credLoading, setCredLoading] = useState(false);
  const [credMsg, setCredMsg] = useState(null);

  useEffect(() => {
    loadDrivers();
    loadVehicleMap();
  }, []);

  const loadDrivers = async () => {
    try {
      const res = await apiEndpoints.drivers.list();
      setDrivers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Drivers only store the assigned vehicle's id — resolve id -> {plate, category}
  // across both permanent and temporary fleets so "Current Vehicle" can show the
  // plate and link to the right page instead of a bare id.
  const loadVehicleMap = async () => {
    try {
      const [permanent, temporary] = await Promise.all([
        apiEndpoints.vehicles.list({ category: 'permanent' }),
        apiEndpoints.vehicles.list({ category: 'temporary' }),
      ]);
      const map = {};
      for (const v of [...(permanent.data || []), ...(temporary.data || [])]) {
        map[v.id] = { plate: v.plate, category: v.category || 'permanent' };
      }
      setVehicleMap(map);
    } catch (err) {
      console.error('Failed to load vehicle map:', err);
    }
  };

  const goToVehicle = (vehicleId) => {
    const info = vehicleMap[vehicleId];
    const path = info?.category === 'temporary' ? '/vehicles/temporary' : '/vehicles';
    navigate(path, { state: { selectedVehicleId: vehicleId } });
  };

  const handleSaveDriver = async (formData) => {
    if (editingDriver) {
      await apiEndpoints.drivers.update(editingDriver.id, formData);
      const res = await apiEndpoints.drivers.list();
      setDrivers(res.data);
      const updated = res.data.find(d => d.id === editingDriver.id);
      if (updated) setSelected(updated);
    } else {
      await apiEndpoints.drivers.create(formData);
      const res = await apiEndpoints.drivers.list();
      setDrivers(res.data);
    }
  };

  const handleDeleteDriver = async () => {
    if (!await confirmDialog(`Delete driver "${selected.name}"? This action cannot be undone.`)) return;
    try {
      await apiEndpoints.drivers.delete(selected.id);
      setSelected(null);
      setCredMsg(null);
      setCredUsername('');
      setCredPassword('');
      const res = await apiEndpoints.drivers.list();
      setDrivers(res.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete driver.');
    }
  };

  const handleSetCredentials = async (e) => {
    e.preventDefault();
    setCredLoading(true);
    setCredMsg(null);
    try {
      await apiEndpoints.drivers.setCredentials(selected.id, { username: credUsername, password: credPassword });
      setCredMsg({ type: 'success', text: `Credentials set. Driver can now log in as "${credUsername}".` });
      setCredPassword('');
    } catch (err) {
      setCredMsg({ type: 'error', text: err.response?.data?.message || 'Failed to set credentials.' });
    } finally {
      setCredLoading(false);
    }
  };

  const getInitials = (name) =>
    (name || 'D').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  const formatDate = (v) => {
    if (!v) return '—';
    return new Date(v).toLocaleDateString();
  };

  const openEdit = () => {
    setEditingDriver(selected);
    setShowDriverForm(true);
  };

  const closeForm = () => {
    setShowDriverForm(false);
    setEditingDriver(null);
  };

  if (loading) return <div className="page-loading">Loading drivers...</div>;

  // ── Detail view ──
  if (selected) {
    const history = Array.isArray(selected.history) ? selected.history : [];
    return (
      <div>
        <button className="back-btn" onClick={() => { setSelected(null); setCredMsg(null); setCredUsername(''); setCredPassword(''); }}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to drivers
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '52px', height: '52px', borderRadius: '50%', backgroundColor: '#0077b6', color: '#fff', fontSize: '20px', fontWeight: 800 }}>
              {getInitials(selected.name)}
            </span>
          </div>
          <div className="vprofile-model">{selected.name}</div>
          <div className="vprofile-sub">{selected.role || 'Fleet driver'} • {selected.company}</div>
        </div>

        <div className="detail-grid">
          <div className="detail-left">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Driver Details</div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={openEdit}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={handleDeleteDriver}>Delete</button>
                  </div>
                )}
              </div>
              <div className="card-body">
                {[
                  ['Name', selected.name],
                  ['Company', selected.company || '—'],
                  ['Role', selected.role || '—'],
                  ['License', selected.license || '—'],
                  ['License Expires', selected.licenseExp ? formatDate(selected.licenseExp) : '—'],
                ].map(([lbl, val]) => (
                  <div key={lbl} className="info-row">
                    <div className="info-lbl">{lbl}</div>
                    <div className="info-val">{val}</div>
                  </div>
                ))}
                <div className="info-row">
                  <div className="info-lbl">Current Vehicle</div>
                  <div className="info-val">
                    {selected.vehicleId ? (
                      <span
                        style={{ color: '#0077b6', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => goToVehicle(selected.vehicleId)}
                      >
                        {vehicleMap[selected.vehicleId]?.plate || selected.vehicleId}
                      </span>
                    ) : 'Unassigned'}
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-lbl">Status</div>
                  <div className="info-val">{selected.status || '—'}</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><div className="card-title">Vehicle History</div></div>
              <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
                {history.length === 0 ? (
                  <div style={{ color: 'var(--text-3)', padding: '12px 16px' }}>No vehicle history yet.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '500px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        {['PLATE', 'VEHICLE', 'ASSIGNED', 'UNASSIGNED', 'DURATION'].map((h) => (
                          <th key={h} style={{ textAlign: 'left', padding: '10px 14px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h, i) => {
                        const from = h.assignedAt ? new Date(h.assignedAt) : null;
                        const to   = h.unassignedAt ? new Date(h.unassignedAt) : null;
                        const days = from && to ? Math.ceil((to - from) / (1000 * 60 * 60 * 24)) : null;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '10px 14px' }}><span className="plate" style={{ fontSize: '12px' }}>{h.plate}</span></td>
                            <td style={{ padding: '10px 14px' }}>{h.brand} {h.model}</td>
                            <td style={{ padding: '10px 14px' }}>{from ? from.toLocaleDateString() : '—'}</td>
                            <td style={{ padding: '10px 14px' }}>{to ? to.toLocaleDateString() : <span style={{ color: '#10b981', fontWeight: 600 }}>Current</span>}</td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-3)' }}>{days !== null ? `${days}d` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <div className="detail-right">
            <div className="card">
              <div className="card-header"><div className="card-title">Stats</div></div>
              <div className="card-body" style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1, textAlign: 'center', background: 'var(--surface-2)', borderRadius: '10px', padding: '14px 10px' }}>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#0077b6' }}>{history.length}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>vehicles driven</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', background: 'var(--surface-2)', borderRadius: '10px', padding: '14px 10px' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: selected.vehicleId ? '#10b981' : '#9ca3af' }}>
                      {selected.vehicleId ? 'Active' : 'Free'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>current status</div>
                  </div>
                </div>
                {selected.vehicleId && (
                  <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 600 }}>Assigned to</div>
                    <div
                      style={{ fontSize: '16px', fontWeight: 700, color: '#0077b6', marginTop: '4px', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => goToVehicle(selected.vehicleId)}
                    >
                      {vehicleMap[selected.vehicleId]?.plate || selected.vehicleId}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {isAdmin && (
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Driver Login Access</div>
                </div>
                <div className="card-body">
                  <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '14px' }}>
                    Set a username and password so this driver can log into the Driver portal.
                  </div>
                  {credMsg && (
                    <div style={{ padding: '9px 12px', borderRadius: '8px', fontSize: '12px', marginBottom: '12px', background: credMsg.type === 'success' ? '#ecfdf5' : '#fff0ef', color: credMsg.type === 'success' ? '#065f46' : '#d93025', border: `1px solid ${credMsg.type === 'success' ? '#6ee7b7' : '#f5b8b4'}` }}>
                      {credMsg.text}
                    </div>
                  )}
                  <form onSubmit={handleSetCredentials}>
                    <div className="form-group">
                      <label className="form-label">Username</label>
                      <input
                        className="form-input"
                        type="text"
                        placeholder="e.g. ion.popescu"
                        value={credUsername}
                        onChange={(e) => setCredUsername(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">New Password</label>
                      <input
                        className="form-input"
                        type="password"
                        placeholder="Min. 6 characters"
                        value={credPassword}
                        onChange={(e) => setCredPassword(e.target.value)}
                        required
                        minLength={6}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary btn-sm" style={{ width: '100%' }} disabled={credLoading}>
                      {credLoading ? 'Saving…' : 'Save Credentials'}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>

        <DriverForm
          isOpen={showDriverForm}
          onClose={closeForm}
          driver={editingDriver}
          onSave={handleSaveDriver}
        />
      </div>
    );
  }

  // ── List view ──
  const visibleDrivers = searchQuery
    ? drivers.filter((d) => {
        const q = searchQuery.toLowerCase();
        return [d.name, d.company, d.license, d.vehicleId, d.role].some((v) => v?.toLowerCase().includes(q));
      })
    : drivers;

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Drivers</div>
          <div className="ph-sub">{drivers.length} driver{drivers.length !== 1 ? 's' : ''} registered</div>
        </div>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => { setEditingDriver(null); setShowDriverForm(true); }}>
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Driver
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          {visibleDrivers.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>No drivers found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '680px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['DRIVER', 'COMPANY', 'LICENSE', 'LICENSE EXP.', 'CURRENT VEHICLE', 'HISTORY', 'STATUS', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleDrivers.map((d) => (
                  <tr key={d._id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => setSelected(d)}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '50%', backgroundColor: '#0077b6', color: '#fff', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>
                          {getInitials(d.name)}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600 }}>{d.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{d.role || 'Fleet driver'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-3)' }}>{d.company || '—'}</td>
                    <td style={{ padding: '12px' }}>{d.license || '—'}</td>
                    <td style={{ padding: '12px' }}>{d.licenseExp ? formatDate(d.licenseExp) : '—'}</td>
                    <td style={{ padding: '12px' }} onClick={(e) => e.stopPropagation()}>
                      {d.vehicleId
                        ? (
                          <span
                            className="plate"
                            style={{ fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => goToVehicle(d.vehicleId)}
                          >
                            {vehicleMap[d.vehicleId]?.plate || d.vehicleId}
                          </span>
                        )
                        : <span style={{ color: 'var(--text-3)' }}>Unassigned</span>}
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-3)' }}>{Array.isArray(d.history) ? d.history.length : 0} vehicle{(Array.isArray(d.history) ? d.history.length : 0) !== 1 ? 's' : ''}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: d.vehicleId ? '#10b981' : '#9ca3af', backgroundColor: d.vehicleId ? '#ecfdf5' : '#f9fafb' }}>
                        {d.vehicleId ? 'Assigned' : 'Available'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); setSelected(d); }}>View →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <DriverForm
        isOpen={showDriverForm}
        onClose={closeForm}
        driver={editingDriver}
        onSave={handleSaveDriver}
      />
    </div>
  );
};

export default Drivers;
